import { memo, useMemo } from "react";
import { AnimatedImage } from "@/components/ui/animated-image";
import { CoworkerIcon } from "@/components/ui/coworker-icon";
import { SectionLabel } from "@/components/ui/section-label";

type UserInfo = {
  display_name: string;
  avatar_url?: string | null;
  is_online?: boolean;
  status?: string | null;
  status_emoji?: string | null;
  is_coworker?: boolean;
};

interface MemberListPanelProps {
  members: readonly { id: string; user_id: string }[];
  userMap: ReadonlyMap<string, UserInfo>;
  /** Called when user clicks a coworker row (admin can open settings). */
  onCoworkerClick?: (coworkerId: string) => void;
}

export const MemberListPanel = memo(function MemberListPanel({
  members,
  userMap,
  onCoworkerClick,
}: MemberListPanelProps) {
  const { humans, coworkers } = useMemo(() => {
    const h: { m: (typeof members)[number]; u: UserInfo }[] = [];
    const c: { m: (typeof members)[number]; u: UserInfo }[] = [];
    for (const m of members) {
      const u = userMap.get(m.user_id);
      if (!u) continue;
      if (u.is_coworker) {
        c.push({ m, u });
      } else {
        h.push({ m, u });
      }
    }
    return { humans: h, coworkers: c };
  }, [members, userMap]);

  const renderMember = ({
    m,
    u,
  }: {
    m: (typeof members)[number];
    u: UserInfo;
  }) => {
    const isCoworker = u.is_coworker === true;
    const isOnline = isCoworker || u.is_online;

    return (
      <div
        key={m.id}
        className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm ${isCoworker && onCoworkerClick ? "cursor-pointer hover:bg-foreground/[0.06]" : ""}`}
        onClick={
          isCoworker && onCoworkerClick
            ? () => onCoworkerClick(m.user_id)
            : undefined
        }
      >
        <div className="relative">
          <div className="h-6 w-6 rounded-full bg-foreground/20 flex items-center justify-center text-[9px] font-semibold overflow-hidden">
            {u.avatar_url ? (
              <AnimatedImage
                src={u.avatar_url}
                alt={u.display_name}
                className="h-full w-full object-cover"
              />
            ) : (
              u.display_name.slice(0, 2).toUpperCase()
            )}
          </div>
          <div
            className={`absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full ${
              isOnline ? "bg-success" : "bg-muted-foreground/30"
            }`}
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-foreground/80">
              {u.display_name}
            </span>
            {isCoworker && <CoworkerIcon size={12} className="shrink-0" />}
            {u.status_emoji && (
              <span className="text-xs shrink-0">{u.status_emoji}</span>
            )}
          </div>
          <p className="text-[10px] text-muted-foreground truncate">
            {isCoworker ? "Online" : u.status}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="w-56 flex flex-col bg-background relative">
      <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-border/95 to-transparent" />
      <div className="h-[38px] shrink-0" />
      <div className="h-12 px-4 flex items-center relative">
        <div className="absolute bottom-0 left-4 right-4 h-px bg-gradient-to-r from-border to-transparent" />
        <span className="font-semibold text-sm">Members</span>
      </div>
      <div className="flex-1 overflow-auto p-2 space-y-0.5">
        {humans.map(renderMember)}
        {coworkers.length > 0 && (
          <>
            <div className="pt-2 pb-1 px-2">
              <SectionLabel variant="menu" className="block">
                AI Coworkers
              </SectionLabel>
            </div>
            {coworkers.map(renderMember)}
          </>
        )}
      </div>
    </div>
  );
});
